/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppRouter, CanLeave, isViewDirty } from '@gh/common-shared';
import { Observable } from 'rxjs';
import { ContextRouteAttachment } from '../router/context-router.interfaces';
import { ContextRouterOutlet } from '../router/context-router.service';
import { CONTEXTABLE_COMPONENT, ContextableComponent } from './contextable-component';

/**
 * This components allows to render context routes into page opened by @angular/router.
 */
@Component({
  selector: 'gh-contextable-page',
  template: '<ng-container #container></ng-container>',
  styles: [`:host > ::ng-deep * {
      flex: 1
  }`],
  providers: [{ provide: CONTEXTABLE_COMPONENT, useExisting: ContextablePageComponent }],
})
export class ContextablePageComponent implements OnInit, OnDestroy, CanLeave, ContextableComponent {
  @ViewChild('container', { read: ViewContainerRef, static: true }) container: ViewContainerRef;

  readonly attachment = ContextRouteAttachment.Router;

  private outlet: ContextRouterOutlet;

  constructor(private router: AppRouter,
              private route: ActivatedRoute) {

  }

  ngOnInit(): void {
    // outlet was created in resolve service to preload data
    const { outlet } = this.route.snapshot.data['context'];

    // attach outlet context lazily
    outlet.attach(this.container);

    this.outlet = outlet;
  }

  ngOnDestroy(): void {
    this.outlet.destroy();
  }

  /**
   * Guarantees that onbeforeunload workflow will work correctly with context routes
   */
  isViewDirty(): boolean {
    return isViewDirty(this.outlet.componentRef.instance);
  }

  /**
   * Guarantees that canDeactivate workflow will work correctly with context routes
   */
  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    return this.outlet.canDeactivate();
  }

  /**
   * There is no any common practice in handling of success page workflow, so nothing to do.
   */
  apply(_: any): void {
    // we ignore provided value for native angular routing
  }

  /**
   * On cancel navigates back
   */
  cancel(): void {
    this.router.navigateBack();
  }
}
